import { useEffect, useState } from 'react';

import { type CompanyGroupDetailResponse, type CompanyResponse } from '../../api';
import {
  AsyncSection,
  DataTable,
  FormError,
  Modal,
  Pagination,
  TextField,
  useNotifications,
  type Column,
} from '../../components';
import { formatCnpj } from '../../format';
import { COMPANY_SEARCH_PAGE_SIZE, useCompanySearch, useSetCompanyGroup } from '../queries';
import { GROUPS_COPY, candidateLabel } from './groups';

/** How long the search box waits after the last keystroke before asking. */
const SEARCH_DEBOUNCE_MS = 300;

/**
 * The "add a company" dialog of one group (#428): a search over the registry,
 * a page of results and the pager, each row with the one action of putting
 * the company in this group.
 *
 * The registry grows with every customer, so it is never read whole here. The
 * search, the page and the "no group" filter go to `GET /companies`, newest
 * registration first; the browser neither filters nor sorts what comes back,
 * with one exception: a company already in *this* group is never offered,
 * which the default filter guarantees by itself and the widened one does not.
 *
 * Off by default, the checkbox widens the request to companies already in
 * another group. Those read through `candidateLabel` -- "(hoje em …)" -- and
 * the sentence that choosing one moves it shows while the box is ticked.
 *
 * The write is `PUT /companies/{id}/group`, the same as the old picker's. On
 * success the dialog closes and the member list is whatever the invalidated
 * `GET /groups/{id}` says; a refusal stays in the dialog, above the results.
 *
 * Mounted only while open (see `Modal`), so closing it discards the search,
 * the page, the checkbox and any refusal on show.
 */
export function AddCompanyDialog({
  group,
  open,
  onClose,
}: {
  group: CompanyGroupDetailResponse;
  open: boolean;
  onClose: () => void;
}) {
  return (
    <Modal open={open} onClose={onClose} title={GROUPS_COPY.addTitle}>
      <AddCompanySearch group={group} onAdded={onClose} />
    </Modal>
  );
}

function AddCompanySearch({
  group,
  onAdded,
}: {
  group: CompanyGroupDetailResponse;
  onAdded: () => void;
}) {
  const assign = useSetCompanyGroup();
  const { notify } = useNotifications();

  const [text, setText] = useState('');
  const search = useDebouncedValue(text.trim(), SEARCH_DEBOUNCE_MS);
  const [includeGrouped, setIncludeGrouped] = useState(false);

  // The page belongs to one search and one filter: a different one starts on
  // the first page, in the same render that sends it, so there is never a
  // request for page 3 of a search that has one.
  const filter = `${includeGrouped ? 'all' : 'ungrouped'}:${search}`;
  const [paging, setPaging] = useState({ filter, page: 1 });
  const page = paging.filter === filter ? paging.page : 1;

  const companies = useCompanySearch({ search, page, withoutGroup: !includeGrouped });

  const candidatesOf = (items: readonly CompanyResponse[]) =>
    items.filter((company) => company.group?.id !== group.id);

  const add = (company: CompanyResponse) => {
    assign.mutate(
      { companyId: company.id, groupId: group.id },
      {
        onSuccess: (saved) => {
          notify('success', `${saved.name} entrou no grupo.`);
          onAdded();
        },
      },
    );
  };

  const columns: readonly Column<CompanyResponse>[] = [
    {
      key: 'name',
      header: GROUPS_COPY.companyLabel,
      render: (row) => candidateLabel(row),
    },
    {
      key: 'slug',
      header: GROUPS_COPY.companySlugLabel,
      render: (row) => <code>{row.slug}</code>,
    },
    {
      key: 'cnpj',
      header: GROUPS_COPY.companyCnpjLabel,
      render: (row) => (row.cnpj === null ? '—' : <code>{formatCnpj(row.cnpj)}</code>),
    },
    {
      key: 'actions',
      header: GROUPS_COPY.actionsLabel,
      align: 'end',
      render: (row) => (
        <button
          type="button"
          className="button button--primary"
          disabled={assign.isPending}
          onClick={() => add(row)}
        >
          {GROUPS_COPY.add}
        </button>
      ),
    },
  ];

  const emptyTitle =
    search !== ''
      ? GROUPS_COPY.noMatches
      : includeGrouped
        ? GROUPS_COPY.noCandidates
        : GROUPS_COPY.noCandidatesUngrouped;
  const emptyDescription =
    search !== ''
      ? includeGrouped
        ? GROUPS_COPY.noMatchesWidenedDescription
        : GROUPS_COPY.noMatchesDescription
      : includeGrouped
        ? undefined
        : GROUPS_COPY.noCandidatesUngroupedDescription;

  return (
    <>
      {/* First in the body, so it is where the dialog puts the focus. */}
      <TextField
        label={GROUPS_COPY.searchLabel}
        hint={GROUPS_COPY.searchHint}
        value={text}
        onChange={setText}
        autoComplete="off"
      />
      <div className="field">
        <label className="field__label">
          <input
            type="checkbox"
            checked={includeGrouped}
            onChange={(event) => setIncludeGrouped(event.target.checked)}
          />{' '}
          {GROUPS_COPY.includeGrouped}
        </label>
        <p className="field__hint">
          {includeGrouped ? GROUPS_COPY.addDescription : GROUPS_COPY.addIntro}
        </p>
      </div>

      {/* A refusal has nowhere to land inside a row, and a toast would take
          the reference code with it. */}
      <FormError error={assign.error} />

      <AsyncSection
        state={companies}
        loadingLabel={GROUPS_COPY.companiesLoading}
        errorTitle={GROUPS_COPY.companiesError}
        isEmpty={(data) => candidatesOf(data.items).length === 0}
        emptyTitle={emptyTitle}
        {...(emptyDescription !== undefined && { emptyDescription })}
      >
        {(data) => (
          <>
            <DataTable
              caption={GROUPS_COPY.candidatesCaption}
              columnVisibility={false}
              columns={columns}
              rows={candidatesOf(data.items)}
              rowKey={(row) => row.id}
            />
            <Pagination
              page={page}
              pageSize={COMPANY_SEARCH_PAGE_SIZE}
              total={data.count}
              onPageChange={(next) => setPaging({ filter, page: next })}
            />
          </>
        )}
      </AsyncSection>
    </>
  );
}

/** `value`, once it has stopped changing for `delayMs`. */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return settled;
}
